<script setup lang="ts">
import {
    ChevronRight,
    Clock3,
    Gift,
    MapPin,
    Pencil,
    Plus,
    ScanLine,
    Trophy,
} from 'lucide-vue-next';
import { computed } from 'vue';
import { locale, t } from '../i18n';
import { money } from '../state';
import type { Network, Profile } from '../types';
import BrandAvatar from './BrandAvatar.vue';

const props = defineProps<{
    profile: Profile;
    networks: Network[];
    activeNetwork: Network | null;
    avatar: string | null;
    fallbackFirstName?: string | null;
}>();

defineEmits<{
    scan: [];
    selectNetwork: [number];
    topUp: [];
    editProfile: [];
    invite: [number];
    loyalty: [number];
    history: [number];
}>();

/** The network's clubs that show «Квести й досягнення» to their players. */
const questClubs = computed(
    () =>
        props.activeNetwork?.clubs.filter((c) => c.quests && !c.blocked) ?? [],
);

const firstName = computed(
    () => props.profile.first_name || props.fallbackFirstName || 'Player',
);
const fullName = computed(() =>
    [firstName.value, props.profile.last_name].filter(Boolean).join(' '),
);

function clubCountLabel(count: number): string {
    if (locale.value === 'en') {
        return `${count} ${t(count === 1 ? 'клуб' : 'клубів')}`;
    }

    const lastTwo = count % 100;
    const last = count % 10;
    const key =
        last === 1 && lastTwo !== 11
            ? 'клуб'
            : last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)
              ? 'клуби'
              : 'клубів';

    return `${count} ${t(key)}`;
}
</script>

<template>
    <section class="dashboard-page">
        <template v-if="activeNetwork">
            <div class="dashboard-canvas dashboard-canvas--network">
                <div class="dashboard-network-heading">
                    <span class="dashboard-network-heading__logo">
                        <BrandAvatar
                            :src="
                                activeNetwork.logo_url ||
                                activeNetwork.clubs[0]?.logo_url
                            "
                            :name="activeNetwork.name"
                            size="sm"
                        />
                    </span>
                    <span class="dashboard-network-heading__name">
                        {{ activeNetwork.name }}
                    </span>
                </div>

                <p class="dashboard-balance">
                    <span class="dashboard-balance__sum">{{
                        money(activeNetwork.deposit, locale)
                    }}</span>
                    <span class="dashboard-balance__currency">₴</span>
                </p>

                <div class="dashboard-actions">
                    <button
                        class="dashboard-button dashboard-button--primary"
                        type="button"
                        @click="$emit('topUp')"
                    >
                        <Plus :size="20" :stroke-width="2.2" />
                        {{
                            activeNetwork.online_topup
                                ? t('Поповнити онлайн')
                                : t('Поповнити')
                        }}
                    </button>
                    <button
                        class="dashboard-button dashboard-button--icon"
                        type="button"
                        :aria-label="t('Історія операцій')"
                        data-test="history-entry"
                        @click="$emit('history', activeNetwork.id)"
                    >
                        <Clock3 :size="20" />
                    </button>
                </div>

                <!-- «Запросити друга» (Черняк, 2026-09-28): on the NETWORK screen, not the
                     home one (owner, 2026-10-06) — the player invites a friend into this
                     network, with this network's link and rule. -->
                <button
                    v-if="activeNetwork.referral_enabled !== false"
                    class="dashboard-invite"
                    type="button"
                    data-test="invite-entry"
                    @click="$emit('invite', activeNetwork.id)"
                >
                    <span class="dashboard-invite__icon" aria-hidden="true">
                        <Gift :size="20" :stroke-width="1.9" />
                    </span>
                    <span class="dashboard-invite__copy">
                        <span class="dashboard-invite__title">{{
                            t('Запросити друга')
                        }}</span>
                        <span class="dashboard-invite__sub">{{
                            t('Бонуси за друзів, які прийдуть у {network}', {
                                network: activeNetwork.name,
                            })
                        }}</span>
                    </span>
                    <ChevronRight :size="18" aria-hidden="true" />
                </button>

                <!-- «Квести й досягнення» (Черняк, 2026-10-15): what this network's clubs
                     pay XP for today, and how far along the player is. -->
                <button
                    v-if="questClubs.length"
                    class="dashboard-invite dashboard-quests"
                    type="button"
                    data-test="quests-entry"
                    @click="$emit('loyalty', questClubs[0].id)"
                >
                    <span class="dashboard-quests__icon" aria-hidden="true">
                        <Trophy :size="20" :stroke-width="2" />
                    </span>
                    <span class="dashboard-invite__copy">
                        <span class="dashboard-invite__title">{{
                            t('Квести й досягнення')
                        }}</span>
                        <span class="dashboard-invite__sub">{{
                            t('Завдання дня, нагороди й ваш рівень')
                        }}</span>
                    </span>
                    <ChevronRight :size="18" aria-hidden="true" />
                </button>
            </div>

            <div class="dashboard-list">
                <header class="dashboard-list__heading">
                    <h2>{{ t('Клуби мережі') }}</h2>
                    <span>{{ activeNetwork.clubs.length }}</span>
                </header>
                <div class="dashboard-divider" />

                <template v-for="club in activeNetwork.clubs" :key="club.id">
                    <component
                        :is="
                            club.quests && !club.blocked ? 'button' : 'article'
                        "
                        class="dashboard-row dashboard-row--club"
                        :class="{
                            'dashboard-row--blocked': club.blocked,
                            'dashboard-row--tap': club.quests && !club.blocked,
                        }"
                        :type="
                            club.quests && !club.blocked ? 'button' : undefined
                        "
                        @click="
                            club.quests &&
                            !club.blocked &&
                            $emit('loyalty', club.id)
                        "
                    >
                        <span class="dashboard-row__logo">
                            <BrandAvatar
                                :src="club.logo_url"
                                :name="club.name"
                            />
                        </span>
                        <span class="dashboard-row__body">
                            <span class="dashboard-row__name">{{
                                club.name
                            }}</span>
                            <span
                                v-if="club.blocked"
                                class="dashboard-row__blocked"
                            >
                                {{ t('Доступ обмежено') }}
                            </span>
                            <span class="dashboard-row__meta">
                                <MapPin
                                    class="dashboard-row__meta-icon"
                                    :size="14"
                                    :stroke-width="1.8"
                                    aria-hidden="true"
                                />
                                <span>{{
                                    club.address || t('Адресу не вказано')
                                }}</span>
                            </span>
                        </span>
                        <span
                            v-if="club.quests && !club.blocked"
                            class="dashboard-row__quests"
                            aria-hidden="true"
                        >
                            <Trophy :size="15" :stroke-width="2" />
                            <ChevronRight :size="16" />
                        </span>
                    </component>
                    <div class="dashboard-divider" />
                </template>

                <footer class="dashboard-list__footer">
                    <button
                        class="dashboard-button dashboard-button--ghost"
                        type="button"
                        @click="$emit('scan')"
                    >
                        <ScanLine :size="20" />
                        {{ t('Приєднатися до клубу') }}
                    </button>
                </footer>
            </div>
        </template>

        <template v-else>
            <div class="dashboard-canvas dashboard-canvas--home">
                <!-- The name and the face open the profile: they are what a person
                     reaches for to correct their own details, and both used to be inert.
                     The pencil is the real control — a focusable button, so the keyboard
                     has a way in too — and tapping the card beside it does the same. -->
                <div class="dashboard-profile" @click="$emit('editProfile')">
                    <BrandAvatar
                        class="dashboard-profile__avatar"
                        :src="avatar"
                        :name="fullName"
                        size="lg"
                        aria-hidden="true"
                    />
                    <div class="dashboard-profile__copy">
                        <!-- The pencil sits on the row of the NAME, so it centres between
                             the two lines of one rather than drifting down when a nickname
                             is there — and at the FAR right, flush with the scan button
                             below, not wherever the surname happens to end. -->
                        <div class="dashboard-profile__name">
                            <h1>
                                <span>{{ firstName }}</span>
                                <span v-if="profile.last_name">{{
                                    profile.last_name
                                }}</span>
                            </h1>
                            <button
                                class="dashboard-profile__edit"
                                type="button"
                                :aria-label="t('Змінити профіль')"
                                @click.stop="$emit('editProfile')"
                            >
                                <Pencil :size="16" :stroke-width="2" />
                            </button>
                        </div>
                        <p v-if="profile.nickname">@{{ profile.nickname }}</p>
                    </div>
                </div>

                <div class="dashboard-actions">
                    <button
                        class="dashboard-button dashboard-button--scan"
                        type="button"
                        @click="$emit('scan')"
                    >
                        <ScanLine :size="20" />
                        {{ t('Сканувати QR') }}
                    </button>
                </div>
            </div>

            <div class="dashboard-list">
                <header class="dashboard-list__heading">
                    <h2>{{ t('Мої мережі') }}</h2>
                    <span v-if="networks.length">{{ networks.length }}</span>
                </header>
                <div class="dashboard-divider" />

                <p v-if="!networks.length" class="dashboard-list__empty">
                    {{
                        t(
                            'Щоб приєднатися до першої мережі, відскануйте її QR-код.',
                        )
                    }}
                </p>

                <template v-for="network in networks" :key="network.id">
                    <button
                        class="dashboard-row dashboard-row--network"
                        type="button"
                        :aria-label="network.name"
                        @click="$emit('selectNetwork', network.id)"
                    >
                        <span class="dashboard-row__logo">
                            <BrandAvatar
                                :src="
                                    network.logo_url ||
                                    network.clubs[0]?.logo_url
                                "
                                :name="network.name"
                            />
                        </span>
                        <span class="dashboard-row__body">
                            <span class="dashboard-row__name">{{
                                network.name
                            }}</span>
                            <span class="dashboard-row__meta">{{
                                clubCountLabel(network.clubs.length)
                            }}</span>
                        </span>
                        <span
                            class="dashboard-row__amount"
                            :class="{ 'is-muted': network.deposit <= 0 }"
                        >
                            {{ money(network.deposit, locale) }} ₴
                        </span>
                    </button>
                    <div class="dashboard-divider" />
                </template>

                <footer v-if="!networks.length" class="dashboard-list__footer">
                    <button
                        class="dashboard-button dashboard-button--ghost"
                        type="button"
                        @click="$emit('scan')"
                    >
                        <ScanLine :size="20" />
                        {{ t('Приєднатися до мережі') }}
                    </button>
                </footer>
            </div>
        </template>
    </section>
</template>

<style scoped>
:global(:root[data-theme='dark']) {
    --dashboard-page-padding: 15px;
    --dashboard-tap: 50px;
    --dashboard-canvas-height: 236px;
    --dashboard-bg: #0a0f1a;
    --dashboard-text: #f2f6ff;
    --dashboard-text-secondary: #6e86a8;
    --dashboard-text-tertiary: #5c6e8a;
    --dashboard-divider: #16202f;
    --dashboard-ghost: #111a2b;
    --dashboard-ghost-text: #8a9ab0;
    --dashboard-muted-amount: #42546f;
    --dashboard-canvas-button: #ffffff;
    --dashboard-canvas-button-text: #0b2e6b;
}

:global(:root[data-theme='light']) {
    --dashboard-page-padding: 15px;
    --dashboard-tap: 50px;
    --dashboard-canvas-height: 236px;
    --dashboard-bg: #ffffff;
    --dashboard-text: #0b1220;
    --dashboard-text-secondary: #7a879b;
    --dashboard-text-tertiary: #a6b0c2;
    --dashboard-divider: #eaeff7;
    --dashboard-ghost: #f1f5fb;
    --dashboard-ghost-text: #5a6779;
    --dashboard-muted-amount: #a6b0c2;
    --dashboard-canvas-button: #0b1220;
    --dashboard-canvas-button-text: #ffffff;
}

.dashboard-page {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 100%;
    flex-direction: column;
    background: var(--dashboard-bg);
    color: var(--dashboard-text);
}

.dashboard-canvas {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 20px;
    padding: calc(22px + var(--app-safe-area-top)) var(--dashboard-page-padding)
        20px;
    /* The same ground as the list below it. This used to be a blue gradient block with
       white text, which cut the screen in two; now the page is one surface and a single
       hairline says where the header ends. Everything that assumed white-on-blue —
       the handle, the currency, the icon button, the avatar ring — moved onto the theme's
       own tokens with it. */
    border-bottom: 1px solid var(--dashboard-divider);
    background: var(--dashboard-bg);
    color: var(--dashboard-text);
}

/* No reserved height: the header is as tall as what is in it.
   It used to hold `--dashboard-canvas-height` (236px, 248px on a wide screen) and push its
   contents to the bottom with `flex-end`. That was the shape of a coloured hero — the empty
   part was the gradient. On the shared background it is just a gap, and on Telegram Desktop,
   where the window is wider, the media query made it 93px of nothing above the avatar. */
.dashboard-canvas--home {
    justify-content: flex-start;
}

/* The balance screen keeps it: heading, sum and buttons are spread through that height on
   purpose, and it has enough in it that the space reads as layout rather than a gap. */
.dashboard-canvas--network {
    min-height: var(--dashboard-canvas-height);
    justify-content: space-between;
}

.dashboard-profile {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 14px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
/* Fills the rest of the row, so the pencil at its end lands on the card's right edge —
   in line with the scan button underneath. */
.dashboard-profile__copy {
    flex: 1;
}
/* The name and its pencil on one line, centred on each other — which puts the pencil
   between the two lines of a two-line name — and pushed apart, so the pencil is at the
   right whatever the name's width. */
.dashboard-profile__name {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}
/* Square, with the same corner the buttons below wear. */
.dashboard-profile__edit {
    display: grid;
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    place-items: center;
    padding: 0;
    border: 0;
    border-radius: 11px;
    background: var(--dashboard-ghost);
    color: var(--dashboard-ghost-text);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.dashboard-profile__edit:active {
    opacity: 0.7;
}

.dashboard-profile__avatar {
    display: grid;
    width: 60px;
    height: 60px;
    flex: 0 0 60px;
    place-items: center;
    overflow: hidden;
    border: 1px solid var(--dashboard-divider);
    border-radius: 50%;
    color: #ffffff;
    font-size: 21px;
    font-weight: 650;
}

.dashboard-profile__copy {
    min-width: 0;
}

.dashboard-profile h1 {
    display: flex;
    min-width: 0;
    flex-direction: column;
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.01em;
    word-break: break-word;
}

.dashboard-profile h1 span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dashboard-profile p {
    margin: 5px 0 0;
    overflow: hidden;
    color: var(--dashboard-text-secondary);
    font-size: 13px;
    /* Not 1. At a line-height of exactly the font size the line box has no room below the
       baseline, and `overflow: hidden` then cuts the descenders clean off — «@ginoexp»
       lost the tails of its g and p. */
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dashboard-network-heading {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 10px;
}

.dashboard-network-heading__logo {
    display: grid;
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    place-items: center;
    border-radius: 11px;
}

.dashboard-network-heading__logo :deep(.brand-avatar) {
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 11px;
}

.dashboard-network-heading__name {
    overflow: hidden;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dashboard-balance {
    display: flex;
    align-items: baseline;
    gap: 7px;
    margin: 0;
}

.dashboard-balance__sum {
    overflow: hidden;
    font-size: 60px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.045em;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dashboard-balance__currency {
    color: var(--dashboard-text-secondary);
    font-size: 26px;
    font-weight: 600;
    line-height: 1;
}

.dashboard-actions {
    display: flex;
    gap: 12px;
}
.dashboard-invite {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 0;
    border-radius: 16px;
    background: var(--dashboard-ghost);
    color: var(--dashboard-ghost-text);
    font: inherit;
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.dashboard-invite:active {
    opacity: 0.8;
}
.dashboard-invite:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
}
.dashboard-invite__icon {
    display: grid;
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    place-items: center;
    border-radius: 12px;
    background: color-mix(in srgb, var(--accent) 16%, transparent);
    color: var(--accent);
}
.dashboard-invite__copy {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 3px;
}
.dashboard-invite__title {
    color: var(--dashboard-text);
    font-size: 15px;
    font-weight: 600;
}
.dashboard-invite__sub {
    font-size: 12px;
    line-height: 1.35;
}
.dashboard-quests {
    position: relative;
    overflow: hidden;
    background:
        radial-gradient(
            120% 140% at 0% 0%,
            color-mix(in srgb, #ffb800 22%, transparent),
            transparent 60%
        ),
        radial-gradient(
            120% 140% at 100% 100%,
            color-mix(in srgb, #b26bff 20%, transparent),
            transparent 60%
        ),
        var(--dashboard-ghost);
}
.dashboard-quests__icon {
    display: grid;
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    place-items: center;
    border-radius: 12px;
    background: linear-gradient(135deg, #ffd66b, #ff9f1c);
    box-shadow: 0 6px 14px rgba(255, 159, 28, 0.35);
    color: #3b2400;
}

.dashboard-button {
    display: flex;
    min-height: var(--dashboard-tap);
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 0 16px;
    border: 0;
    border-radius: 16px;
    font-size: 15px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
}

.dashboard-button:focus-visible,
.dashboard-row--network:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
}

.dashboard-button--primary {
    min-width: 0;
    flex: 1.6 1 0;
    background: var(--dashboard-canvas-button);
    color: var(--dashboard-canvas-button-text);
}

.dashboard-button--icon {
    width: var(--dashboard-tap);
    flex: 0 0 var(--dashboard-tap);
    padding: 0;
    background: var(--dashboard-ghost);
    color: var(--dashboard-ghost-text);
}

.dashboard-button--scan {
    flex: 1 1 auto;
    background: var(--dashboard-canvas-button);
    color: var(--dashboard-canvas-button-text);
}

.dashboard-button--ghost {
    width: 100%;
    background: var(--dashboard-ghost);
    color: var(--dashboard-ghost-text);
    font-weight: 500;
}

.dashboard-list {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 6px;
    padding: 26px var(--dashboard-page-padding)
        calc(24px + env(safe-area-inset-bottom));
}

.dashboard-list__heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding-bottom: 14px;
}

.dashboard-list__heading h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 1;
}

.dashboard-list__heading > span {
    color: var(--dashboard-text-tertiary);
    font-size: 16px;
    line-height: 1;
}

.dashboard-divider {
    height: 1px;
    flex: 0 0 1px;
    background: var(--dashboard-divider);
}

.dashboard-list__empty {
    max-width: 270px;
    margin: 20px 0 0;
    color: var(--dashboard-text-secondary);
    font-size: 15px;
    line-height: 1.5;
}

.dashboard-row {
    display: flex;
    width: 100%;
    min-height: 92px;
    align-items: center;
    gap: 14px;
    padding: 14px 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
}

.dashboard-row--network {
    cursor: pointer;
}

.dashboard-row--club {
    min-height: 92px;
}

.dashboard-row--tap {
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.dashboard-row__quests {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 2px;
    color: #ff9f1c;
}

.dashboard-row__quests svg:last-child {
    color: var(--dashboard-text-secondary);
}

.dashboard-row--club .dashboard-row--blocked .dashboard-row__logo,
.dashboard-row--blocked .dashboard-row__name,
.dashboard-row--blocked .dashboard-row__meta {
    /* Dimmed, not hidden. The club stays on the list because the person is still its
       client — they simply cannot use it until the club says otherwise, and a club that
       vanished would read as a bug in the app rather than a decision by the club. */
    opacity: 0.55;
}

.dashboard-row__blocked {
    margin-top: 3px;
    color: var(--danger);
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
}

.dashboard-row__meta {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-size: 13px;
    line-height: 1.3;
}

.dashboard-row__meta-icon {
    flex: 0 0 auto;
    margin-top: 1px;
}

.dashboard-row__logo {
    display: grid;
    width: 52px;
    height: 52px;
    flex: 0 0 52px;
    place-items: center;
    border-radius: 16px;
}

.dashboard-row__logo :deep(.brand-avatar) {
    width: 52px;
    height: 52px;
    border: 0;
    border-radius: 16px;
}

.dashboard-row__body {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 7px;
}

.dashboard-row__name {
    overflow: hidden;
    color: var(--dashboard-text);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dashboard-row__meta {
    color: var(--dashboard-text-secondary);
    font-size: 14px;
    line-height: 1.35;
}

.dashboard-row__amount {
    flex: 0 0 auto;
    color: var(--dashboard-text);
    font-size: 21px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
}

.dashboard-row__amount.is-muted {
    color: var(--dashboard-muted-amount);
}

.dashboard-list__footer {
    margin-top: auto;
    padding-top: 20px;
}

@media (max-width: 379px) {
    .dashboard-page {
        --dashboard-page-padding: 12px;
        --dashboard-canvas-height: 224px;
    }

    .dashboard-balance__sum {
        font-size: 54px;
    }

    .dashboard-profile__avatar {
        width: 54px;
        height: 54px;
        flex-basis: 54px;
    }
}

@media (min-width: 421px) {
    .dashboard-page {
        --dashboard-page-padding: 18px;
        --dashboard-canvas-height: 248px;
    }

    .dashboard-balance__sum {
        font-size: 66px;
    }
}

@media (prefers-reduced-motion: no-preference) {
    .dashboard-button,
    .dashboard-row--network,
    .dashboard-row--tap {
        transition: opacity 0.15s ease;
    }

    .dashboard-button:active,
    .dashboard-row--network:active,
    .dashboard-row--tap:active {
        opacity: 0.72;
    }
}
</style>
